import { ReactNode, createContext, useEffect, useRef, useState } from "react";
import { ApiMethod } from "../common/models/api-types.type";
import { User } from "../common/models/user.model";
import { useAuthApi } from "../common/hooks/use-auth-api.hook";
import AuthClientStore, { ACCESS_TOKEN_KEY } from "../common/services/auth-client.service";
import { SESSION_ENDED_EVENT } from "../common/services/device-session.service";
import { endSupportSessionLocally, SUPPORT_SESSION_ENDED_EVENT, supportReturnPath } from "../common/services/support-session.service";
import { supportFrameSessionId } from "../common/services/support-frame";
import { routes } from "../assets/constants";
import { afterSessionEnded } from "../router/account-paths";
import { jwtDecode } from 'jwt-decode';
import { useLocation, useNavigate } from "react-router-dom";
import { config, resetConfig } from "../common/hooks/use-settings-api.hook";
import { cn } from "@/lib/utils";
import { ArdealyLoader } from "../components/design-system/common/ArdealyLoader";
import { waitForNetworkQuiet } from "../common/services/network-activity.service";
import { persistor } from "../redux/store";
import { dropPushSubscription } from "../common/services/web-push.service";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

type ContextType = {
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  user: User
  login(email: string, password: string): Promise<void>;
  /** Signs in and returns the account without switching the app over yet. */
  loginWithoutRedirect(email: string, password: string): Promise<User>;
  /** Switches the app over to the account `loginWithoutRedirect` returned. */
  completeAuthentication(user?: User): void;
  /** `remote: false` when the API already ended the session (nothing to tell it). */
  logout(options?: { remote?: boolean }): void;
  me(): Promise<User>;
  sendAuthGuardedRequest(
    method: ApiMethod,
    path: string,
    body?: any,
    init?: RequestInit,
  ): Promise<unknown>;
  setGlobalLoading(loading: boolean, message?: string): void;
};

export const AuthContext = createContext<ContextType | undefined>(undefined);

/**
 * Who a token signs in as: the account and its vendor. A refreshed token keeps
 * it; signing in as someone else changes it.
 */
const tokenIdentity = (token: string | null): string | null => {
  if (!token) return null;
  try {
    const claims = jwtDecode<any>(token);
    return `${claims.username || claims.sub || ''}|${claims.vuuid || ''}`;
  } catch {
    return null;
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [checkIsDone, setCheckIsDone] = useState(false);
  const [user, setUser] = useState<User>();
  const location = useLocation()
  const navigate = useNavigate()
  const [globalLoading, setGlobalLoadingState] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [pendingUser, setPendingUser] = useState<User | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const { t } = useTranslation();

  // Every tab shares one token in localStorage, and every request reads it when
  // it is sent. Signing in as another vendor in a second tab therefore turned an
  // open tab into that vendor without its page changing — a Stark rug saved from
  // the editor landed in Bâti Orient. The tab now notices, and stops.
  const sessionIdentity = useRef<string | null>(null);
  const [sessionChanged, setSessionChanged] = useState<'switched' | 'signedOut' | null>(null);

  useEffect(() => {
    sessionIdentity.current = isAuthenticated ? tokenIdentity(AuthClientStore.getAccessToken()) : null;
  }, [isAuthenticated, user]);

  useEffect(() => {
    // `storage` fires only in the OTHER tabs, never the one that wrote.
    const onStorage = (e: StorageEvent) => {
      if (e.key !== ACCESS_TOKEN_KEY && e.key !== null) return;
      const mine = sessionIdentity.current;
      if (!mine) return;
      const now = tokenIdentity(AuthClientStore.getAccessToken());
      if (now !== mine) setSessionChanged(now ? 'switched' : 'signedOut');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setGlobalLoading = (loading: boolean, message: string = "") => {
    if (!loading && globalLoading) {
      // Start closing animation
      setIsClosing(true);
      setTimeout(() => {
        setGlobalLoadingState(false);
        setLoadingMessage("");
        setIsClosing(false);
      }, 400); // Match fadeOut animation duration
    } else {
      setGlobalLoadingState(loading);
      setLoadingMessage(message);
    }
  };

  const {
    login: authLogin,
    logout: authLogout,
    signout: authSignout,
    me: authMe,
    sendAuthGuardedRequest: authSendAuthGuardedRequest,
  } = useAuthApi();


  const checkAccess = async() => {
    // A support tab: its own short token, no refresh. Once it runs out, the tab
    // goes back to the super admin.
    if (AuthClientStore.isSupportSession()) {
      const expiresAt = AuthClientStore.supportExpiresAt();
      if (!expiresAt || expiresAt <= Date.now()) {
        endSupportSessionLocally();
        return;
      }
      if (!user) await me();
      setCheckIsDone(true);
      return;
    }
    if(AuthClientStore.getRefreshToken()){
      const decodedRefreshToken = jwtDecode<any>(AuthClientStore.getRefreshToken()); 
      if(decodedRefreshToken.exp*1000 < new Date().getTime()){
        logout()
      } else {
        if(!user)
          await me()
      }
    } 
    else {
      logout()
    }
    setCheckIsDone(true)
  }

  useEffect(() => {
      checkAccess()
  }, [location])

  // The API ended this device's session (signed in elsewhere, signed out from
  // the Devices page, seat taken). The login page explains why.
  useEffect(() => {
    // Leave for the login page: the provider only re-checks on navigation, so
    // without it the tab would sit on an empty page.
    const onEnded = () => {
      logout({ remote: false });
      if (afterSessionEnded(window.location.pathname) === 'stay') {
        // Nothing left to check signed out; keep the page on screen.
        setCheckIsDone(true);
        return;
      }
      navigate('/login', { replace: true });
    };
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The support session in this frame is over. The super admin's page closes
  // the frame; outside one (never expected) go back to the vendor's history.
  useEffect(() => {
    const onSupportEnded = (e: Event) => {
      if (supportFrameSessionId()) return;
      const vendorId = (e as CustomEvent).detail?.vendorId;
      window.location.assign(supportReturnPath(vendorId));
    };
    window.addEventListener(SUPPORT_SESSION_ENDED_EVENT, onSupportEnded);
    return () => window.removeEventListener(SUPPORT_SESSION_ENDED_EVENT, onSupportEnded);
  }, [])

  useEffect(() => {
    // Clear the loading overlay once sign-in has landed somewhere real and that
    // page has finished its first round of requests, so the app is revealed
    // loaded rather than blank. Not a fixed address: a vendor is sent on from
    // /dashboard to the workspace they last used, which could be any of them.
    if (isAuthenticated && !['/', '/login'].includes(location.pathname) && globalLoading) {
      let cancelled = false;
      let timer: number | undefined;
      waitForNetworkQuiet().then(() => {
        if (cancelled) return;
        setIsClosing(true);
        timer = window.setTimeout(() => {
          setGlobalLoadingState(false);
          setLoadingMessage("");
          setIsClosing(false);
        }, 400); // Match fadeOut animation duration
      });
      return () => {
        cancelled = true;
        window.clearTimeout(timer);
      };
    }
  }, [location.pathname, isAuthenticated, globalLoading])
  
  const login = async (email: string, password: string): Promise<void> => {
    try {
      await authLogin(email, password)
      await me()
    } catch (e) {
      setIsAuthenticated(false);
    }
  };

  const loginWithoutRedirect = async (email: string, password: string): Promise<User> => {
    try {
      await authLogin(email, password)
      const userData = await authMe(() => {
        setIsAuthenticated(false);
      });
      // Store user data but don't set authentication yet
      setPendingUser(userData);
      return userData;
    } catch (e) {
      setIsAuthenticated(false);
      throw e;
    }
  };

  // Takes the account as an argument: a caller that awaited the sign-in still
  // holds this function from before `pendingUser` was set, and would read null.
  const completeAuthentication = (signedIn: User | null = pendingUser) => {
    if (signedIn) {
      setIsAuthenticated(true);
      setIsSuperAdmin(signedIn?.roles[0] === "SUPER_ADMIN");
      setUser(signedIn);
      setPendingUser(null);
      // The overlay, if one is up, is cleared by the effect above once the
      // landing page has loaded.
    }
  };

  /** Signing out of a support tab ends the support session, and the super admin stays signed in. */
  const endSupportSession = async () => {
    try {
      await authSendAuthGuardedRequest(() => undefined, ApiMethod.POST, routes.supportSessions.endCurrent);
    } catch {
      // Already over: nothing to end.
    }
    endSupportSessionLocally();
  };

  const logout = (options?: { remote?: boolean }) => {
    if (AuthClientStore.isSupportSession()) {
      void endSupportSession();
      return;
    }
    // Free this device's seat now rather than when it goes idle. Sent before the
    // tokens are cleared: the request reads the access token as it is created.
    if (options?.remote !== false && AuthClientStore.getAccessToken()) {
      void authSignout();
    }
    // This browser stops receiving the account's push notifications; the
    // account's "on" choice is kept and restored at its next sign-in here.
    void dropPushSubscription();
    authLogout();
    setIsAuthenticated(false);
    setIsSuperAdmin(false);
    setUser(null);
    setPendingUser(null);
    setCheckIsDone(false);
    resetConfig();
    // Purge all persisted Redux state to ensure vendor data is cleared
    persistor.purge();
  };

  const me = async () => {
    const user = await authMe(() => {
      setIsAuthenticated(false);
    });
    setIsAuthenticated(true);
    setIsSuperAdmin(user?.roles[0] === "SUPER_ADMIN")
    setUser(user)
    return user;
  };

  const sendAuthGuardedRequest = async (
    method: ApiMethod,
    path: string,
    // eslint-disable-next-line
    body?: any,
    init?: RequestInit,
  ) => {
    return authSendAuthGuardedRequest(
      () => {
        setIsAuthenticated(false);
      },
      method,
      path,
      body,
      init,
    );
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isSuperAdmin,
        user,
        login,
        loginWithoutRedirect,
        completeAuthentication,
        logout,
        me,
        sendAuthGuardedRequest,
        setGlobalLoading,
      }}
    >
      {checkIsDone && children}

      {/* Cannot be dismissed: anything done in this tab now would be done as
          whoever signed in elsewhere. */}
      <Dialog open={!!sessionChanged}>
        <DialogContent
          showCloseButton={false}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="z-[10001]"
          overlayClassName="z-[10001]"
        >
          <DialogHeader>
            <DialogTitle>
              {sessionChanged === 'signedOut'
                ? t('AUTH_SESSION.SIGNED_OUT_TITLE', 'You signed out in another tab')
                : t('AUTH_SESSION.SWITCHED_TITLE', 'You signed in to another account')}
            </DialogTitle>
            <DialogDescription>
              {sessionChanged === 'signedOut'
                ? t('AUTH_SESSION.SIGNED_OUT_BODY', 'This tab is no longer signed in. Reload to continue.')
                : t('AUTH_SESSION.SWITCHED_BODY', 'Another tab signed in to a different account. Anything saved here would go to that account, so reload this tab before continuing.')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => window.location.reload()}>
              {t('AUTH_SESSION.RELOAD', 'Reload')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global Loading Overlay */}
      {globalLoading && (
        <div
          className={cn(
            'fixed inset-0 z-[10000] flex items-center justify-center bg-background',
            isClosing ? 'ardealy-overlay-out' : 'ardealy-overlay-in',
          )}
        >
          <div className="flex flex-col items-center justify-center">
            <ArdealyLoader className="mb-6 w-12" />
            <p
              key={loadingMessage}
              className="ardealy-overlay-text text-sm text-muted-foreground"
            >
              {loadingMessage}
            </p>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}